import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {zodResolver} from "@hookform/resolvers/zod";
import {RamonagePdfReport, pdfService} from "@mytour/pdf";
import {useContext, useCurrentCompany, useCurrentUserStyle} from "@mytour/platform";
import type {CertificatRamonageDetails} from "@mytour/shared";
import {getLabel} from "@mytour/shared";
import {getEmailStatusBadgeState} from "@mytour/shared";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import axios from "axios";
import {useEffect, useRef, useState} from "react";
import {useForm} from "react-hook-form";
import {Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner} from "reactstrap";
import {useDownloadCertificate} from "../actions/useDownloadCertificate";
import {ApplianceRecapCard} from "../components/ApplianceRecapCard";
import {
  useCertificate,
  useCompleteCertificate,
  useResendCertificate,
} from "../query/certificateQueries";
import type {CertificateRecapProps} from "../types";
import {RecapEmailForm, getRecapEmailDefaultValues, recapEmailSchema} from "./RecapEmailForm";
import type {RecapEmailFormData} from "./RecapEmailForm";
import {isApplianceRecapAvailable} from "./isApplianceRecapAvailable";

type Props = CertificateRecapProps;

export function CertificateRecapModal(props: Props) {
  return <RecapContent key={props.eventId} {...props} />;
}

function RecapContent({eventId, customerId, fileName, onClose, customerQueries}: Props) {
  const {context} = useContext();
  const {company} = useCurrentCompany();
  const {platformUserStyle} = useCurrentUserStyle();
  const {
    certificate,
    isCertificateLoading,
    isCertificateFetching,
    certificateError,
    refetchCertificate,
  } = useCertificate(eventId, true, "always");
  const {downloadCertificate, isDownloadingCertificate, downloadCertificateError} =
    useDownloadCertificate(eventId, fileName);
  const badgeState = getEmailStatusBadgeState(certificate?.emailStatus);
  const canSend =
    !!certificate?.hasFile && !!badgeState && !isCertificateFetching && !certificateError;
  const {
    data: customer,
    isLoading: isCustomerLoading,
    isFetching: isCustomerFetching,
    error: customerError,
    refetch: refetchCustomer,
  } = useQuery(
    customerQueries.detail(customerId, canSend || certificate?.hasFile === false, {staleTime: 0})
  );
  const {resendCertificate, isResendingCertificate} = useResendCertificate();
  const {completeCertificate, isCompletingCertificate} = useCompleteCertificate();
  const queryClient = useQueryClient();
  const {mutateAsync: updateCustomer, isPending: isUpdatingCustomer} = useMutation(
    customerQueries.update(queryClient)
  );
  const [error, setError] = useState<string | null>(null);
  const sending = useRef(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: {errors},
  } = useForm<RecapEmailFormData>({
    resolver: zodResolver(recapEmailSchema),
    defaultValues: getRecapEmailDefaultValues(),
  });
  useEffect(() => {
    reset({email: customer?.email ?? ""});
  }, [customer?.email, reset]);

  const isPending = isResendingCertificate || isUpdatingCustomer || isCompletingCertificate;
  const isEmailDisabled = isPending || isCustomerLoading || isCustomerFetching || !!customerError;
  const hasEmail = !!customer?.email;
  const needsEmailCorrection = badgeState === "failed" || !hasEmail;
  const isEmailUncertain = badgeState === "uncertain";
  const canEditCustomer =
    context?.role === "manager" ||
    (context?.role === "worker" && context.permissions.canEditCustomer);

  return (
    <Modal isOpen toggle={handleClose} scrollable>
      <ModalHeader toggle={handleClose}>
        Attestation de ramonage
        <div className="small text-muted mt-1 fw-normal">Récapitulatif</div>
      </ModalHeader>
      <ModalBody>
        {isCertificateLoading ? (
          <div role="status">
            <Spinner size="sm" aria-hidden="true" /> Chargement du récapitulatif…
          </div>
        ) : certificateError || !isApplianceRecapAvailable(certificate?.appareils) ? (
          <Alert color="warning">
            Le récapitulatif de cette attestation est indisponible. Vous pouvez télécharger le PDF
            existant.
            <div className="mt-2">
              <Button color="link" onClick={handleRetry} disabled={isCertificateFetching}>
                {isCertificateFetching && <Spinner size="sm" />} Réessayer
              </Button>
            </div>
          </Alert>
        ) : (
          <ApplianceRecapCard appareils={certificate.appareils} />
        )}

        {certificate?.hasFile !== false ? (
          <Button
            color="primary"
            className="w-100 mb-3"
            onClick={downloadCertificate}
            disabled={isDownloadingCertificate}
          >
            {isDownloadingCertificate ? (
              <Spinner size="sm" />
            ) : (
              <FontAwesomeIcon icon="file-pdf" />
            )}
            &nbsp; Télécharger l'attestation
          </Button>
        ) : (
          certificate && (
            <Alert color="warning">
              Document enregistré, PDF à compléter.
              <div className="mt-2">
                <Button
                  color="primary"
                  type="button"
                  onClick={handleResumePdf}
                  disabled={
                    isPending ||
                    isCustomerLoading ||
                    isCustomerFetching ||
                    !!customerError ||
                    !customer ||
                    !company ||
                    !platformUserStyle
                  }
                >
                  {isCompletingCertificate && <Spinner size="sm" className="me-2" />}
                  Reprendre le PDF
                </Button>
              </div>
              {customerError && (
                <Button color="link" type="button" onClick={handleRetryCustomer}>
                  Réessayer le chargement du client
                </Button>
              )}
            </Alert>
          )
        )}
        {downloadCertificateError && (
          <Alert color="danger" role="alert">
            {downloadCertificateError}
          </Alert>
        )}

        {isEmailUncertain && (
          <Alert color="warning">
            <strong>Envoi à vérifier</strong>
            <br />
            Nous ne pouvons pas confirmer si l’email est parti. Un renvoi peut créer un doublon.
          </Alert>
        )}

        {canSend &&
          (isCustomerLoading || isCustomerFetching ? (
            <div role="status">
              <Spinner size="sm" aria-hidden="true" /> Chargement des informations d'envoi…
            </div>
          ) : customerError || !customer ? (
            <Alert color="warning">
              Les informations d'envoi sont indisponibles. Le récapitulatif et le PDF restent
              accessibles.
              <div className="mt-2">
                <Button color="link" onClick={handleRetryCustomer} disabled={isCustomerFetching}>
                  Réessayer le chargement du client
                </Button>
              </div>
            </Alert>
          ) : needsEmailCorrection ? (
            <>
              <Alert color="danger">
                <strong>{hasEmail ? "Email non envoyé" : "Adresse email manquante"}</strong>
                <br />
                {hasEmail ? (
                  "L'attestation n'a pas pu être envoyée à cette adresse."
                ) : (
                  <>
                    {getLabel(customer)}
                    <b>{" n'a aucun email"}</b>
                    {" renseigné."}
                  </>
                )}
              </Alert>
              {canEditCustomer ? (
                <RecapEmailForm control={control} errors={errors} disabled={isEmailDisabled} />
              ) : (
                <p className="small">
                  {hasEmail
                    ? `Adresse actuelle : ${customer.email}. Un responsable autorisé peut la corriger ; vous pouvez renvoyer à cette adresse.`
                    : "Un responsable autorisé doit ajouter une adresse email."}
                </p>
              )}
            </>
          ) : (
            <p className="small">
              Un email avec l'attestation sera envoyé à{" "}
              <a href={`mailto:${customer.email}`}>{customer.email}</a>.
            </p>
          ))}
        {error && (
          <Alert color="danger" role="alert">
            {error}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={isPending}>
          ← Retour
        </Button>
        {canSend && customer && !customerError && (
          <Button
            color="primary"
            onClick={
              needsEmailCorrection && canEditCustomer
                ? handleSubmit(handleCorrectAndSend)
                : handleSend
            }
            disabled={isEmailDisabled || (!canEditCustomer && !hasEmail)}
          >
            {isPending && <Spinner size="sm" />}{" "}
            {needsEmailCorrection
              ? canEditCustomer
                ? "Corriger et envoyer"
                : "Renvoyer"
              : isEmailUncertain
                ? "Renvoyer"
                : "Envoyer par email"}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );

  function handleClose() {
    if (!isPending && !sending.current) {
      onClose();
    }
  }

  async function handleRetry() {
    await refetchCertificate();
  }

  async function handleRetryCustomer() {
    await refetchCustomer();
  }

  async function handleResumePdf() {
    if (!certificate || certificate.hasFile || !customer || !company || !platformUserStyle) {
      return;
    }
    try {
      setError(null);
      await completeCertificate({
        eventId,
        sendEmail: false,
        customer,
        generatePdf: (savedCertificate: CertificatRamonageDetails) =>
          pdfService.toBlob(
            <RamonagePdfReport
              data={savedCertificate}
              company={company}
              customer={customer}
              intervenantName={savedCertificate.intervenantName ?? undefined}
              pdfStyle={pdfService.getStyle(platformUserStyle)}
            />
          ),
      });
      await refetchCertificate();
    } catch {
      setError("La reprise du PDF a échoué. Réessayez sans recréer l'attestation.");
    }
  }

  async function handleSend() {
    if (!canSend || isEmailDisabled || !hasEmail || sending.current) {
      return;
    }
    sending.current = true;
    try {
      setError(null);
      applyResendResult(await resendCertificate(eventId));
    } catch (sendError) {
      setError(buildSendError(sendError));
    } finally {
      sending.current = false;
    }
  }

  async function handleCorrectAndSend(data: RecapEmailFormData) {
    if (!canSend || !canEditCustomer || isEmailDisabled || sending.current) {
      return;
    }
    sending.current = true;
    try {
      setError(null);
      const fresh = await refetchCustomer();
      if (fresh.error || !fresh.data) {
        setError(
          "Impossible de récupérer la fiche client à jour. Réessayez avant de corriger l'email."
        );
        return;
      }
      if (data.email !== fresh.data.email) {
        await updateCustomer({...fresh.data, email: data.email});
      }
      applyResendResult(await resendCertificate(eventId));
    } catch (sendError) {
      setError(buildSendError(sendError));
    } finally {
      sending.current = false;
    }
  }

  function applyResendResult(result: {emailStatus: string; emailMessage?: string}) {
    if (result.emailStatus === "sent") {
      onClose();
    } else if (result.emailStatus === "uncertain") {
      setError("Envoi à vérifier. Un nouveau renvoi peut créer un doublon.");
    } else {
      setError(result.emailMessage ?? "L'envoi n'a pas abouti. Vérifiez l'adresse et réessayez.");
    }
  }
}

function buildSendError(error: unknown): string {
  if (axios.isAxiosError(error) && error.response?.status === 404) {
    return "L'attestation stockée est introuvable. Contactez le support pour pouvoir l'envoyer.";
  }
  return "Envoi à vérifier. Nous ne pouvons pas confirmer si l'email est parti. Un renvoi peut créer un doublon.";
}
